/**
 * Copyright(c) Live2D Inc. All rights reserved.
 *
 * Use of this source code is governed by the Live2D Open Software license
 * that can be found at https://www.live2d.com/eula/live2d-open-software-license-agreement_en.html.
 */

import { LAppDelegate } from './lappdelegate';
import LAppDefine from './lappdefine';
import { LAppGlManager } from './lappglmanager';
import {LAppLive2DManager} from "./lapplive2dmanager";
import {LAppMessageBox} from "./lappmessagebox";

/**
 * 終了時の処理
 */
window.addEventListener(
    'beforeunload',
    (): void => LAppDelegate.releaseInstance(),
    { passive: true }
);

/**
 * Process when changing screen size.
 */
window.addEventListener(
    'resize',
    () => {
        if (LAppDefine.CanvasSize === 'auto') {
            LAppDelegate.getInstance().onResize();
        }
    },
    { passive: true }
);

// 启动入口
function launchLive2d(config: Live2dConfig) {
    // Initialize WebGL and create the application instance
    const keys = Object.keys(config);
    for (const key of keys) {
        if (!!key) {
            if (LAppDefine.hasOwnProperty(key)) {
                Reflect.set(LAppDefine, key, Reflect.get(config, key));
            }
        }
    }

    if (
        !LAppGlManager.getInstance() ||
        !LAppDelegate.getInstance().initialize(config.Model)
    ) {
        return;
    }

    LAppDelegate.getInstance().run();
}

// 修改画板
function changeScene(model: string) {
    var instance = LAppLive2DManager.getInstance(model, false);
    if (!!instance) {
        instance.changeScene(model);
    }
}

// 清除实例
function releaseInstance() {
    LAppDelegate.releaseInstance()
}

// 执行动作
function setExpression(model: string, expressionId: string): void {
    const instance = LAppLive2DManager.getInstance(model, false);
    if (!instance) {
        return
    }

    const laModel = instance.getModel(model);
    if (!!laModel) {
        laModel.setExpression(expressionId);
    }
}

// 获取所有动作
function getAllExpression(model: string): string[] {
    const result: string[] = []
    const instance = LAppLive2DManager.getInstance(model, false);
    if (!instance) {
        return result
    }

    const laModel = instance.getModel(model);
    const size = laModel._expressions.getSize();
    for (let i = 0; i < size; i++) {
        const key = laModel._expressions._keyValues[i].first
        result.push(key)
    }

    return result
}

// 随机动作
function setRandomExpression(model: string): void {
    const instance = LAppLive2DManager.getInstance(model, false);
    if (!instance) {
        return
    }

    const laModel = instance.getModel(model);
    if (!!laModel) {
        laModel.setRandomExpression();
    }
}

// 嘴巴动画
function mouthMotions(model: string, pathOrBuffer: string | ArrayBuffer, voiceWeight: number = 0.8): void {
    const instance = LAppLive2DManager.getInstance(model, false);
    if (!instance) {
        return
    }

    const laModel = instance.getModel(model);
    if (!!laModel) {
        laModel.mouthMotions(pathOrBuffer, voiceWeight)
    }
}

// 加载文件
function loadVoice(model: string, path: string): Promise<ArrayBuffer> {
    const instance = LAppLive2DManager.getInstance(model, false);
    if (!instance) {
        return Promise.reject(new Error('No instance'));
    }

    const laModel = instance.getModel(model);
    if (!!laModel) {
        return laModel.loadVoice(path)
    }

    return Promise.reject(new Error('No laModel'));
}

function setMessageBox(message: string, duration: number) {
    const messageBox = LAppMessageBox.getInstance();
    messageBox.setMessage(message, duration);
}

function hideMessageBox() {
    const messageBox = LAppMessageBox.getInstance();
    messageBox.hideMessageBox();
}

function revealMessageBox() {
    const messageBox = LAppMessageBox.getInstance();
    messageBox.revealMessageBox();
}

const Live2d = {
    launchLive2d,
    changeScene,
    releaseInstance,
    setExpression,
    setRandomExpression,
    getAllExpression,
    mouthMotions,
    loadVoice,
    setMessageBox,
    hideMessageBox,
    revealMessageBox,
}

if (typeof window !== 'undefined') {
    (window as any).Live2d = Live2d;
}

export default Live2d